import { useCallback, useEffect, useMemo } from 'react'
import { FormikHelpers } from 'formik'
import { message } from 'antd'
import { useParams } from 'react-router-dom'

import { useBackgroundNavigate } from '@shared/hooks'
import { useQuery } from '@shared/modules/use-query/hooks'
import { UseQueryOptions } from '@shared/modules/use-query/types'
import { FormType } from '@shared/modules/form/types'
import { localURLMaker } from '@shared/utils/url-maker'

import { CustomerFormData } from '../../customers/types'
import { CustomersService } from '../../customers/services'
import { CustomerMappers } from '../../customers/mappers'
import { AppointmentRoutes } from '../constants'

export const useAppointmentCustomerForm = () => {
  const params = useParams<{ userId?: string }>()
  const navigate = useBackgroundNavigate()
  const baseInitialValues = useMemo<CustomerFormData>(
    () => ({
      firstname: '',
      lastname: '',
      patronymic: '',
      nationality: 'Azərbaycanlı',
      phoneNumber: '',
      email: '',
      passportSecret: '',
      gender: null,
      regionId: '',
      insuranceNumber: '',
      franchise: '',
      birthDate: null,
      statusId: '5',
      description: '',
    }),
    [],
  )

  const { reset, error, ...initialValues } = useQuery(
    useCallback(async () => {
      const result = await CustomersService.getOneById(params.userId as string)

      if (result.status === 200) {
        return CustomerMappers.formInitializer(result.data)
      } else {
        return baseInitialValues
      }
    }, [baseInitialValues, params.userId]),
    useMemo<UseQueryOptions>(
      () => ({
        initialData: baseInitialValues,
        enabled: !!params.userId,
      }),
      [baseInitialValues, params.userId],
    ),
  )

  const onSubmit = useCallback(
    async (values: CustomerFormData, helpers: FormikHelpers<CustomerFormData>) => {
      helpers.setStatus(null)
      let newType = params.userId ? 'edit' : 'create'
      const result = await CustomersService.createOrUpdate(values, newType)
      if (result.status === 200) {
        const { id } = result.data
        if (newType === 'create') {
          navigate(localURLMaker(AppointmentRoutes.CREATE_BY_USER_ID, { userId: id }))
        }
        message.success('Müştəri müvəffəqiyyətlə əlavə edildi.')
      } else if (result.status === 422) {
        helpers.setErrors(result.data)
      } else {
        helpers.setStatus({ type: 'error', message: result.data })
      }
    },
    [navigate, params.userId],
  )

  const type = useMemo<FormType>(() => (!!params.userId ? 'read' : 'create'), [params.userId])

  useEffect(() => {
    reset()
  }, [params.userId, reset])

  return { initialValues, onSubmit, type }
}
